import { EMAIL_REGEX } from '../constants/regex';
import {
  MAX_DRIVERS_LICENSE_SIZE_MB,
  MAX_DRIVERS_LICENSE_SIZE_MB_STRING,
} from '@app/constants/documents';
import { StepperVariant } from './stepper';
import { validatePhoneWithCountryCode } from '../utils/transformPhoneNumber';
import { ValidateUSZipCode } from '../utils/checkout';
import { z, ZodObject } from 'zod';

const isMaskedData = (value: string) => {
  return value?.includes('*') || false;
};

export enum CreditApplicationType {
  GUEST_USER = 'GUEST_USER',
  LOGGED_IN_USER = 'LOGGED_IN_USER',
}

export enum YearsInBusiness {
  LESS_THAN_5_YEARS = 'LESS_THAN_5_YEARS',
  GREATER_THAN_EQUAL_TO_6_YEARS = 'GREATER_THAN_EQUAL_TO_6_YEARS',
}

export const salesContactSchema = z.discriminatedUnion('hasSalesContact', [
  z.object({
    hasSalesContact: z.literal(true),
    firstName: z
      .string()
      .min(1, 'First name is required')
      .max(128, 'Please limit your input to 128 characters.'),
    lastName: z
      .string()
      .min(1, 'Last name is required')
      .max(128, 'Please limit your input to 128 characters.'),
    email: z
      .string()
      .trim()
      .max(255, 'Please limit your input to 255 characters.')
      .refine(
        (val) => {
          return !val || isMaskedData(val) || EMAIL_REGEX.test(val);
        },
        {
          message: 'Invalid email address',
        },
      )
      .optional(),
  }),
  z.object({
    hasSalesContact: z.literal(false),
  }),
]);

export const companyDetailsFormSchema = z.object({
  salesContact: salesContactSchema,
  businessLegalName: z
    .string()
    .trim()
    .min(1, 'Business legal name is required')
    .max(128, 'Please limit your input to 128 characters.'),
  yearsInBusiness: z.enum(
    [YearsInBusiness.LESS_THAN_5_YEARS, YearsInBusiness.GREATER_THAN_EQUAL_TO_6_YEARS],
    {
      required_error: 'Years in business is required',
    },
  ),
  dba: z.string().trim().max(128, 'Please limit your input to 128 characters.').optional(),
  locationId: z.number().nullable().optional(),
  address: z
    .string()
    .trim()
    .min(1, 'Address is required')
    .max(256, 'Please limit your input to 256 characters.'),
  city: z
    .string()
    .trim()
    .min(1, 'City is required')
    .max(256, 'Please limit your input to 256 characters.'),
  state: z.string().min(1, 'State is required'),
  zip: z.string().min(1, 'Zip is required').refine(ValidateUSZipCode, 'Invalid zip code'),
  federalTaxID: z
    .string()
    .trim()
    .refine((val) => {
      const digitsOnly = val.replace(/-/g, '');
      return digitsOnly.length === 9 && /^\d{9}$/.test(digitsOnly);
    }, 'Federal tax ID must be 9 digits'),
  natureOfBusiness: z
    .string()
    .trim()
    .min(1, 'Nature of business is required')
    .max(128, 'Please limit your input to 128 characters.'),
  stateOfOrganization: z.string().min(1, 'State of organization is required'),
  contactName: z
    .string()
    .trim()
    .min(1, 'Contact name is required')
    .max(128, 'Please limit your input to 128 characters.'),
  phone: z
    .string()
    .min(1, 'Mobile number is required')
    .max(32, 'Please limit your input to 32 characters.')
    .refine(
      (val) => !val || isMaskedData(val) || validatePhoneWithCountryCode(val),
      'Invalid mobile number. Must be 10 digits or include +1 followed by 10 digits.',
    ),
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .max(255, 'Please limit your input to 255 characters.')
    .refine((val) => !val || isMaskedData(val) || EMAIL_REGEX.test(val), 'Invalid email address'),
  isSalesTaxExempt: z.boolean().default(false),
  hasFiledBankruptcy: z.boolean().default(false),
  branchId: z.number().optional(),
});

export const billingDetailsFormSchema = z.object({
  accountsPayableFirstName: z
    .string()
    .min(1, 'Accounts payable first name is required')
    .max(128, 'Please limit your input to 128 characters.'),
  accountsPayableLastName: z
    .string()
    .min(1, 'Accounts payable last name is required')
    .max(128, 'Please limit your input to 128 characters.'),
  billingAddress: z
    .string()
    .min(1, 'Address is required')
    .max(128, 'Please limit your input to 128 characters.'),
  city: z
    .string()
    .min(1, 'City is required')
    .max(128, 'Please limit your input to 128 characters.'),
  state: z.string().min(1, 'State is required'),
  zip: z.string().min(1, 'Zip is required').refine(ValidateUSZipCode, 'Invalid zip code'),
  billingPhone: z
    .string()
    .min(1, 'Mobile number is required')
    .max(32, 'Please limit your input to 32 characters.')
    .refine(
      (val) => !val || isMaskedData(val) || validatePhoneWithCountryCode(val),
      'Invalid mobile number. Must be 10 digits or include +1 followed by 10 digits.',
    ),
  billingEmail: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .max(255, 'Please limit your input to 255 characters.')
    .refine((val) => !val || isMaskedData(val) || EMAIL_REGEX.test(val), 'Invalid email address'),
  preferredMethodOfInvoicing: z.string(),
  isPORequired: z.boolean(),
  isMonthlyStatementsRequired: z.boolean(),
});

export const insuranceDocumentSchema = z.object({
  insuranceType: z.string().min(1, 'Insurance type is required'),
  insuranceFile: z.string().min(1, 'Insurance file is required'),
});

export const insuranceDetailsFormSchema = z.discriminatedUnion('haveMyOwnRentalInsurance', [
  z.object({
    haveMyOwnRentalInsurance: z.literal(true),
    insuranceCompany: z
      .string()
      .min(1, 'Insurance company is required')
      .max(128, 'Please limit your input to 128 characters.'),
    firstName: z
      .string()
      .min(1, 'First name is required')
      .max(128, 'Please limit your input to 128 characters.'),
    lastName: z
      .string()
      .min(1, 'Last name is required')
      .max(128, 'Please limit your input to 128 characters.'),
    phone: z
      .string()
      .min(1, 'Mobile number is required')
      .refine(
        (val) => !val || isMaskedData(val) || validatePhoneWithCountryCode(val),
        'Invalid mobile number. Must be 10 digits or include +1 followed by 10 digits.',
      ),
    email: z
      .string()
      .min(1, 'Email is required')
      .max(255, 'Please limit your input to 255 characters.')
      .email('Invalid email address'),
  }),
  z.object({
    haveMyOwnRentalInsurance: z.literal(false),
  }),
]);

export const personalGuaranteeFormSchemaComponents = {
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(255, 'Please limit your input to 255 characters.'),
  ssn: z
    .string()
    .min(1, 'SSN is required')
    .refine((val) => {
      const digitsOnly = val.replace(/-/g, '');
      return digitsOnly.length === 9 && /^\d{9}$/.test(digitsOnly);
    }, 'SSN must be 9 digits'),
  address: z
    .string()
    .trim()
    .min(1, 'Address is required')
    .max(128, 'Please limit your input to 128 characters.'),
  city: z
    .string()
    .trim()
    .min(1, 'City is required')
    .max(128, 'Please limit your input to 128 characters.'),
  state: z.string().trim().min(1, 'State is required'),
  zipCode: z
    .string()
    .trim()
    .min(1, 'Zip code is required')
    .refine((val) => !val || ValidateUSZipCode(val), 'Invalid zip code'),
};

export const driversLicenseFileSchema = z
  .instanceof(File, { message: 'No file selected. Please choose a PDF, JPEG, or PNG file.' })
  .refine(
    (file) => ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(file.type),
    { message: 'Unsupported file type. Upload a PDF, JPEG or PNG' },
  )
  .refine((file) => file.size <= MAX_DRIVERS_LICENSE_SIZE_MB, {
    message: `File too large. Maximum file size is ${MAX_DRIVERS_LICENSE_SIZE_MB_STRING}`,
  });

export const driverLicenseDocumentSchema = z.discriminatedUnion('isRequired', [
  z.object({
    file: driversLicenseFileSchema,
    isRequired: z.literal(true),
  }),
  z.object({
    file: driversLicenseFileSchema.optional(),
    isRequired: z.literal(false),
  }),
]);

export const personalGuaranteeFormSchema = z.discriminatedUnion('isRequired', [
  z.object({
    name: personalGuaranteeFormSchemaComponents.name,
    ssn: personalGuaranteeFormSchemaComponents.ssn,
    address: personalGuaranteeFormSchemaComponents.address,
    city: personalGuaranteeFormSchemaComponents.city,
    state: personalGuaranteeFormSchemaComponents.state,
    zipCode: personalGuaranteeFormSchemaComponents.zipCode,
    driversLicense: driverLicenseDocumentSchema,
    isRequired: z.literal(true),
  }),
  z.object({
    name: personalGuaranteeFormSchemaComponents.name.or(z.literal('')),
    ssn: personalGuaranteeFormSchemaComponents.ssn.or(z.literal('')),
    address: personalGuaranteeFormSchemaComponents.address.or(z.literal('')),
    city: personalGuaranteeFormSchemaComponents.city.or(z.literal('')),
    state: personalGuaranteeFormSchemaComponents.state.or(z.literal('')).or(z.undefined()),
    zipCode: personalGuaranteeFormSchemaComponents.zipCode.or(z.literal('')),
    driversLicense: driverLicenseDocumentSchema,
    isRequired: z.literal(false),
  }),
]);

export const reviewFormSchema = z.object({
  termsAndConditionsSignerName: z
    .string()
    .min(1, 'Name is required')
    .max(255, 'Please limit your input to 255 characters.'),
  personalGuarantee: personalGuaranteeFormSchema,
});

export const creditFormSchema: CreditFormSchemaType = z.object({
  companyDetails: companyDetailsFormSchema,
  billingDetails: billingDetailsFormSchema,
  insuranceDetails: insuranceDetailsFormSchema,
  review: reviewFormSchema,
});

export type CreditFormModel = z.infer<typeof creditFormSchema>;
export type CompanyFormModel = z.infer<typeof companyDetailsFormSchema>;
export type BillingFormModel = z.infer<typeof billingDetailsFormSchema>;
export type InsuranceFormModel = z.infer<typeof insuranceDetailsFormSchema>;
export type ReviewFormModel = z.infer<typeof reviewFormSchema>;

export type OwnInsuranceFormModel = Extract<InsuranceFormModel, { haveMyOwnRentalInsurance: true }>;

export type HasSalesContactModel = Extract<
  z.infer<typeof salesContactSchema>,
  { hasSalesContact: true }
>;

export type CreditFormSchemaType = ZodObject<{
  [key in CreditFormKey]: CreditSubFormSchemaType;
}>;

export type CreditSubFormSchemaType =
  | typeof companyDetailsFormSchema
  | typeof insuranceDetailsFormSchema
  | typeof billingDetailsFormSchema
  | typeof reviewFormSchema;

export type FieldName = keyof CreditFormModel;

export type CreditFormSubVariant =
  | CompanyFormModel
  | InsuranceFormModel
  | BillingFormModel
  | ReviewFormModel;

export type CreditFormKey = 'companyDetails' | 'billingDetails' | 'insuranceDetails' | 'review';

// Increased Credit Form Types
export const increaseCreditFormSchema = z.object({
  currentCreditLimit: z.number().min(1, 'Current credit limit is required'),
  requestedCreditLimit: z.number().min(1, 'Requested credit limit is required'),
  reasonForCreditIncrease: z.string().min(1, 'Reason for Credit Increase is required'),
});

export type IncreaseCreditFormModel = z.infer<typeof increaseCreditFormSchema>;

// Config Types
export type CreditStepKey = CreditFormKey;

export type CreditConfig = {
  stepperConfig: CreditStepperConfig;
  stepperOrder: CreditStepKey[];
  stepperStatus: CreditApplicationStatus[];
};

export type CreditStepperConfig = {
  [key in CreditStepKey]: CreditStepConfig;
};

export type CreditStepConfig = {
  showInStepper?: boolean | StepperVariant;
  stepLabel?: string;
  backLabel?: string;
  isComplete?: boolean;
  key?: CreditStepKey;
  schema?: CreditSubFormSchemaType;
  fields?: string[];
};

//State Types
export type CreditState = {
  [key in string]: CreditSessionState;
};

export type CreditSessionState = {
  stepper: CreditStepperState;
  status?: CreditApplicationStatus;
  error?: string;
};

export type CreditStepperState = {
  [key in CreditStepKey]: CreditStepState;
};

export type CreditStepState = {
  isComplete?: boolean;
  formData?: Partial<CreditFormSubVariant>;
};
export interface SaveFormDataPayload {
  stepKey: CreditStepKey;
  formData: Partial<CreditFormSubVariant>;
}

export interface ResetStepperProgressPayload {
  stepKey: CreditStepKey;
}

export type CreditApplicationStatusResponse = {
  id: string | null;
  status: CreditApplicationStatus;
  credit_limit: number | null;
};

export type CreditApplicationBranch = {
  id: number;
  branch_id: number;
  account_rep_user_id: number | null;
  name: string;
  description: string | null;
  phone_number: string;
  state_id: number;
  active: boolean;
  location_id: number;
  company_id: number;
  is_public_msp: boolean;
  abbreviation: string;
  default_branch: boolean;
  service_email: string;
  sales_email: string;
};

export type CreditApplicationRequest = {
  id?: string | null;
  status: CreditApplicationStatus;
  recaptcha_token: string;
  company_details?: {
    contact_name: string;
    contact_phone: string;
    contact_email: string;
    business_legal_name: string;
    federal_tax_id: string;
    is_sales_exempt: boolean;
    in_bankruptcy: boolean;
    sales_staff_exists?: boolean;
    sales_person_first_name?: string;
    sales_person_last_name?: string;
    sales_person_email?: string;
    branch_id?: number;
  };
  billing_details?: {
    account_payable_contact_first_name?: string;
    account_payable_contact_last_name?: string;
    billing_address_street_1?: string;
    billing_address_city?: string;
    billing_address_state_id?: number;
    billing_address_zip_code?: string;
    billing_phone?: string;
    billing_email?: string;
    is_paperless_billing?: string;
    is_po_required?: boolean;
    disable_monthly_statements?: boolean;
  };
  insurance_details?: {
    own_insurance?: boolean;
    insurance_company?: string;
    insurance_contact_first_name?: string;
    insurance_contact_last_name?: string;
    insurance_phone?: string;
    insurance_email?: string;
  };
  branch?: CreditApplicationBranch;
  years_in_business?: YearsInBusiness | null;
  submitting_user_name?: string;
  personal_guarantee_ssn?: string;
  personal_guarantee_username?: string;
  personal_guarantee_address?: string;
  personal_guarantee_city?: string;
  personal_guarantee_state_id?: number;
  personal_guarantee_zip_code?: string;
  drivers_license_document_id?: number;
};

export type CreditApplicationResponse = CreditApplicationRequest & {
  id: string | null;
  status: CreditApplicationStatus;
  credit_limit: number | null;
  created_by_user_id: number;
  updated_by_user_id: number;
  date_created: string;
  date_updated: string;
};

export type GuestCreditApplicationRequest = {
  company_details?: {
    contact_name: string;
    contact_phone: string;
    contact_email: string;
    federal_tax_id: string;
    is_sales_exempt: boolean;
    in_bankruptcy: boolean;
    sales_staff_exists?: boolean;
    sales_person_first_name?: string;
    sales_person_last_name?: string;
    sales_person_email?: string;
    branch_id?: number;
    business_legal_name: string;
    years_in_business: string;
    business_nature: string;
    org_state: number;
    doing_business_as?: string;
    company_address_zip_code: string;
    company_address_state_id: number;
    company_address_city: string;
    company_address_street_1: string;
  };
  billing_details?: {
    account_payable_contact_first_name?: string;
    account_payable_contact_last_name?: string;
    billing_address_street_1?: string;
    billing_address_city?: string;
    billing_address_state_id?: number;
    billing_address_zip_code?: string;
    billing_phone?: string;
    billing_email?: string;
    is_paperless_billing?: string;
    is_po_required?: boolean;
    disable_monthly_statements?: boolean;
  };
  insurance_details?: {
    own_insurance?: boolean;
    insurance_company?: string;
    insurance_contact_first_name?: string;
    insurance_contact_last_name?: string;
    insurance_phone?: string;
    insurance_email?: string;
  };
  submitting_user_name: string;
  recaptcha_token: string;
  status: CreditApplicationStatus;
  personal_guarantee_ssn?: string;
  personal_guarantee_username?: string;
  personal_guarantee_address?: string;
  personal_guarantee_city?: string;
  personal_guarantee_state_id?: number;
  personal_guarantee_zip_code?: string;
};

export type GuestCreditApplicationResponse = GuestCreditApplicationRequest & {
  id: string;
  status: CreditApplicationStatus;
  credit_limit: number | null;
  date_updated: string;
};

export enum PreferredMethodOfInvoicing {
  EMAIL = 'EMAIL',
  BOTH = 'BOTH',
}

export enum CreditApplicationStatus {
  NOT_STARTED = 'NOT_STARTED',
  STEP1_SAVED = 'STEP1_SAVED',
  STEP2_SAVED = 'STEP2_SAVED',
  STEP3_SAVED = 'STEP3_SAVED',
  SUBMITTED = 'SUBMITTED',
}

export const CREDIT_FORM_KEYS = {
  COMPANY_DETAILS: 'companyDetails' as const,
  BILLING_DETAILS: 'billingDetails' as const,
  INSURANCE_DETAILS: 'insuranceDetails' as const,
  REVIEW: 'review' as const,
};

export const CreditApplicationStatusTypeID = {
  NOT_STARTED: 0,
  STEP1_SAVED: 1,
  STEP2_SAVED: 2,
  STEP3_SAVED: 3,
  SUBMITTED: 3,
} as const;

export interface CreditApplicationCompanyDetails {
  businessLegalName: string;
  dba: string;
  locationId: number | null;
  address: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  zip: string;
  federalTaxID: string;
  natureOfBusiness: string;
  stateOfOrganization: string;
  contactName: string;
  phone: string;
  email: string;
  isSalesTaxExempt: boolean;
  hasFiledBankruptcy: boolean;
  salesContact?: {
    hasSalesContact: boolean;
    firstName: string;
    lastName: string;
    email: string;
  };
  yearsInBusiness?: YearsInBusiness;
  branchId?: number;
}

export interface CreditApplicationBillingDetails {
  accountsPayableFirstName: string;
  accountsPayableLastName: string;
  billingPhone: string;
  billingEmail: string;
  billingAddress: string;
  city: string;
  state: string;
  zip: string;
  preferredMethodOfInvoicing: string;
  isPORequired: boolean;
  isMonthlyStatementsRequired: boolean;
}
export interface CreditApplicationInsuranceDetails {
  haveMyOwnRentalInsurance: boolean;
  firstName: string;
  lastName: string;
  insuranceCompany: string;
  phone: string;
  email: string;
}
export interface CreditApplicationData {
  creditApplicationStatus: CreditApplicationStatusResponse;
  companyDetails: CreditApplicationCompanyDetails;
  billingDetails: CreditApplicationBillingDetails;
  insuranceDetails: CreditApplicationInsuranceDetails;
  dateSubmitted: string;
}

export interface CreditApplicationSummary {
  companyDetails?: CreditApplicationCompanyDetails;
  billingDetails?: CreditApplicationBillingDetails;
  insuranceDetails?: CreditApplicationInsuranceDetails;
}

export type CreditFetchType = 'status' | 'details';
